import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ProtectedRoute } from "./components/ProtectedRoute";

// Lazy-loaded pages — each chunk loads only when the route is visited
const HomePage = lazy(() => import("./pages/HomePage"));
const AboutSOLAPage = lazy(() => import("./pages/AboutSOLAPage"));
const TrainingPage = lazy(() => import("./pages/TrainingPage"));
const DistributionPage = lazy(() => import("./pages/DistributionPage"));
const HattrixPage = lazy(() => import("./pages/HattrixPage"));
const NxWitnessPage = lazy(() => import("./pages/NxWitnessPage"));
const AzorAnalyticsPage = lazy(() => import("./pages/AzorAnalyticsPage"));
const KantechTrainingPage = lazy(() => import("./pages/KantechTrainingPage"));
const KantechExamPage = lazy(() => import("./pages/KantechExamPage"));
const KantechFirmwarePage = lazy(() => import("./pages/KantechFirmwarePage"));
const HattrixCertificationPage = lazy(() => import("./pages/HattrixCertificationPage"));
const NxResourcesPage = lazy(() => import("./pages/NxResourcesPage"));
const NxProjectRegistrationPage = lazy(() => import("./pages/NxProjectRegistrationPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const BecomePartnerPage = lazy(() => import("./pages/BecomePartnerPage"));
const InitializePasswordPage = lazy(() => import("./pages/InitializePasswordPage"));
const RegistrationSuccessPage = lazy(() => import("./pages/RegistrationSuccessPage"));
const DealerPortal = lazy(() => import("./pages/DealerPortal"));
const DealerProfile = lazy(() => import("./pages/DealerProfile"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

// Minimal full-screen loader shown while a page chunk downloads
const PageLoader = () => (
  <div className="min-h-screen bg-[#0B0E11] flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      <span className="text-sm text-zinc-500 font-mono tracking-wider">SOLAGROUP</span>
    </div>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutSOLAPage />} />
              <Route path="/training" element={<TrainingPage />} />
              <Route path="/distribution" element={<DistributionPage />} />
              <Route path="/hattrix" element={<HattrixPage />} />
              <Route path="/nx-vms" element={<NxWitnessPage />} />
              <Route path="/solutions/azor-analytics" element={<AzorAnalyticsPage />} />
              <Route path="/solutions/kantech-training" element={<KantechTrainingPage />} />
              <Route path="/solutions/kantech-training/exam" element={<KantechExamPage />} />
              <Route path="/solutions/hattrix/certification" element={<HattrixCertificationPage />} />
              <Route path="/hattrix/support" element={<KantechFirmwarePage />} />
              <Route path="/hattrix/support/kantech-firmware" element={<KantechFirmwarePage />} />
              <Route path="/solutions/nx-witness/resources" element={<NxResourcesPage />} />
              <Route path="/solutions/nx-witness/project-registration" element={<NxProjectRegistrationPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/become-partner" element={<BecomePartnerPage />} />
              <Route path="/initialize-password" element={<InitializePasswordPage />} />
              <Route path="/auth/initialize" element={<InitializePasswordPage />} />
              <Route path="/registration-success" element={<RegistrationSuccessPage />} />
              <Route path="/portal" element={<ProtectedRoute><DealerPortal /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><DealerProfile /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
